import React, { ReactNode, useEffect } from 'react';
import {
  AccordionItem as SherpaAccordionItem,
  AccordionItemButton as SherpaAccordionItemButton,
  AccordionItemButtonIcon as SherpaAccordionItemButtonIcon,
  AccordionItemButtonTitle as SherpaAccordionItemButtonTitle,
  AccordionItemContent as SherpaAccordionItemContent,
} from '../../../../deps/@bshgroup/component-accordion';
import { ChevronDown as SherpaChevronDown } from '../../../../deps/@bshgroup/component-icons';
import { domAnimation, LazyMotion } from 'framer-motion';
import { useAccordionControl } from '../Accordion/AccordionControlContext';
import { useTranslation } from '../../../contexts/TranslationContext/TranslationContext';
import { Heading } from '../Heading/Heading';
import { MarkdownViewer } from '../../MarkdownViewer/MarkdownViewer';

export interface FAQProps {
  /**
   * Element id, required for tracking opened FAQs
   */
  id: string;
  /**
   * FAQ title/question
   */
  title: ReactNode;
  /**
   * FAQ description/answer
   */
  description?: ReactNode;
  /**
   * Raw content additional to text description
   */
  children?: ReactNode;
  /**
   * Initial expanded state
   */
  initialExpanded?: boolean;
}

/**
 * The `FAQ` displays a question and answer to be used inside an `Accordion`.
 */
export function FAQ({
  id,
  title,
  description,
  children,
  initialExpanded = false,
  ...props
}: FAQProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof FAQProps>) {
  const { t } = useTranslation();
  const { isExpanded, toggleExpanded, setExpanded } = useAccordionControl();

  useEffect(() => {
    setExpanded(id, initialExpanded);
  }, []);

  return (
    <div key={id} {...props}>
      <SherpaAccordionItem
        size="md"
        extended={isExpanded(id)}
        button={config => (
          <SherpaAccordionItemButton
            {...config}
            title={config => (
              <SherpaAccordionItemButtonTitle
                {...config}
                css={{ textAlign: 'left' }}
                headline={() => <Heading style="title">{title}</Heading>}
              />
            )}
            aria-label={t('DSSF.Components.Core.FAQ.ExpandButton')}
            icon={config => <SherpaAccordionItemButtonIcon {...config} as={SherpaChevronDown} />}
            onClick={() => toggleExpanded(id)}
          />
        )}
        content={() => (
          <LazyMotion features={domAnimation}>
            <SherpaAccordionItemContent size="md" extended={isExpanded(id)} css={{ whiteSpace: 'pre-line' }}>
              {description && <MarkdownViewer value={description as string} />}
              {children}
            </SherpaAccordionItemContent>
          </LazyMotion>
        )}
      />
    </div>
  );
}
